import type { ColumnDef } from '../config/viewsConfig';
import type { EntityType } from '../types';
import { resolveColumnOrderFromKeys } from './resolveColumnFromUrlKey';

const PREFIX = 'lims-cols:';

function columnsMatchDefault(visible: ColumnDef[], all: ColumnDef[]): boolean {
  if (visible.length !== all.length) return false;
  for (let i = 0; i < visible.length; i++) {
    if (visible[i].key !== all[i].key) return false;
  }
  return true;
}

/**
 * Read persisted column keys from localStorage, returning the matching
 * ColumnDefs in stored order. Returns null if nothing is stored or all
 * stored keys have been removed from the schema.
 */
/**
 * @param entityType When set, keys not in `allColumns` can still resolve (e.g. JSON picker columns).
 */
export function readColumns(
  storageKey: string,
  allColumns: ColumnDef[],
  entityType?: EntityType,
): ColumnDef[] | null {
  try {
    let raw = localStorage.getItem(`${PREFIX}${storageKey}`);
    if (!raw) {
      const legacyKey = storageKey.replace(/^lab\d+:/, '');
      if (legacyKey !== storageKey) {
        raw = localStorage.getItem(`${PREFIX}${legacyKey}`);
      }
    }
    if (!raw) return null;
    const keys: string[] = JSON.parse(raw);
    if (entityType) {
      const restored = resolveColumnOrderFromKeys(keys, entityType, allColumns);
      return restored.length > 0 ? restored : null;
    }
    const byKey = new Map(allColumns.map(c => [c.key, c]));
    const restored = keys.map(k => byKey.get(k)).filter((c): c is ColumnDef => c != null);
    return restored.length > 0 ? restored : null;
  } catch {
    return null;
  }
}

/**
 * Write column keys to localStorage. Removes the key entirely when the
 * selection matches the full default set (keeps storage clean).
 */
export function writeColumns(storageKey: string, cols: ColumnDef[], allColumns: ColumnDef[]): void {
  if (columnsMatchDefault(cols, allColumns)) {
    localStorage.removeItem(`${PREFIX}${storageKey}`);
  } else {
    localStorage.setItem(`${PREFIX}${storageKey}`, JSON.stringify(cols.map(c => c.key)));
  }
}
